import { useTranslation } from 'react-i18next'
import type { EvidenceStatusLogEntry } from '../../../types/evidence-numbers'

interface EvidenceStatusTimelineProps {
  entries: EvidenceStatusLogEntry[]
  loading: boolean
}

function formatField(field: string): string {
  return field.replace(/^is_/, '').replaceAll('_', ' ')
}

function TimelineEntry({ entry }: { entry: EvidenceStatusLogEntry }): React.ReactElement {
  const date = new Date(entry.createdAt)

  return (
    <div className="relative flex gap-3 pb-6 last:pb-0">
      <div className="flex flex-col items-center">
        <div className="h-3 w-3 rounded-full bg-[var(--accent)] ring-2 ring-[var(--bg-card)]" />
        <div className="w-px flex-1 bg-[var(--border)]" />
      </div>
      <div className="flex-1 pb-2">
        <p className="text-sm font-medium text-[var(--text-primary)]">
          {formatField(entry.statusField)}: {entry.oldValue ? 'true' : 'false'} &rarr;{' '}
          {entry.newValue ? 'true' : 'false'}
        </p>
        <p className="mt-0.5 text-xs text-[var(--text-muted)]">
          {date.toLocaleDateString()} {date.toLocaleTimeString()} &middot; {entry.changeSource}
        </p>
        {entry.notes ? (
          <p className="mt-1 text-xs text-[var(--text-secondary)]">{entry.notes}</p>
        ) : null}
      </div>
    </div>
  )
}

export function EvidenceStatusTimeline({
  entries,
  loading,
}: EvidenceStatusTimelineProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  if (loading) {
    return <div className="h-32 animate-pulse rounded bg-[var(--bg-card)]" />
  }

  if (entries.length === 0) {
    return (
      <p className="text-sm text-[var(--text-muted)] italic">{t('detail.statusTimeline')}: --</p>
    )
  }

  return (
    <div className="space-y-0">
      {entries.map((entry) => (
        <TimelineEntry key={entry.id} entry={entry} />
      ))}
    </div>
  )
}
